// Session PJ2: the engineer's PROJECT SURVEY visit on the phone. One visit covers the site survey and one survey per lift
// (a modernisation, a full replacement or a new lift). Phone first and offline first, on the SafedTestSheet pattern: the
// whole visit rides the job's completion payload (projectSurvey), so a survey finished in a plant room with no signal
// reaches the platform whole when the outbox drains.
//
// THE FLOW. The visit screen (the project, the site, the office's access note, the RAMS acceptance as on every job, the
// times, then the list: "Site" first, each lift with its type and progress, "Add another lift here"); a lift asks "What
// are you surveying?" first and then shows only that type's sections; a section shows only the fields that apply (and
// only the follow-up questions whose answer calls for them); "Next" goes to the next section with anything left. Finish
// lists, per survey, what is left blank or could not be accessed, so nothing is hidden, and the ONLY things that stop a
// submit are a started lift's type and its number of floors (the Director's rule), and a reason for any survey left.
//
// SAVING. Every change is a CELL stamped with this phone's clock at the moment of the change, saved to the device at once
// (src/lib/offline/surveyStore.ts) and, with signal, sent at most every 10 seconds (a background request: the Session ASB
// idle rule). On open, the server's copy and this phone's cells are MERGED per path, the newer winning, never a whole
// overwrite either way (src/lib/projectSurveyDraft.ts). A visit can stop and be continued later on this or another phone.
//
// No price anywhere, and nothing about the customer's contact: the visit view carries neither.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, Ruler } from "lucide-react";
import { api, ApiError, type ProjectSurveyVisitView } from "../../lib/api";
import { useEngineerData } from "./EngineerDataContext";
import { TimeOnSiteCard } from "./JobDetail";
import { markSurveyLiftCreated, queuedPhotosForJob } from "../../lib/offline/photosStore";
import { retryEscalated } from "../../lib/offline/jobsStore";
import type { QueuedPhoto } from "../../lib/offline/types";
import {
  SURVEY_NOTICES_EVENT,
  clearSurveyVisit,
  loadSurveyVisit,
  readSurveyPhotoNotices,
  saveSurveyDraft,
  saveSurveyVisit,
} from "../../lib/offline/surveyStore";
import {
  NOT_SURVEYED_REASONS,
  NOT_SURVEYED_REASON_CODES,
  SITE_LABEL,
  afterSave,
  buildProjectSurveyPayload,
  cachedSurveyFrom,
  effectiveRecord,
  effectiveType,
  emptySurveyDraft,
  emptyVisitDraft,
  isSurveyType,
  nextSectionWithWork,
  noAccessCell,
  pendingSave,
  phoneMadeId,
  photoSlotKeys,
  planSurvey,
  reviewedSurveyChanged,
  pruneInapplicable,
  sectionState,
  surveyGaps,
  surveyProgress,
  valueCell,
  visibleFields,
  visibleSections,
  withCell,
  withType,
  type CachedVisitSurvey,
  type NotSurveyedReason,
  type SurveyDraftRow,
  type SurveyVisitDraftRow,
  type VisitSurveyState,
} from "../../lib/projectSurveyDraft";
import { FLOORS_COUNT_FIELD, SURVEY_FORM_VERSION, SURVEY_TYPES, SURVEY_TYPE_CODES, findSection, type SurveyType } from "../../lib/surveyTemplate";
import { SURVEY_NOTES_PATH, emptyRecord, fieldPath, notesPath, readRecord, sectionPath, valueAt, type SurveyCell } from "../../lib/surveyRecord";
import {
  defaultVisitYmd,
  isoToLocalYmd,
  localYmd,
  pastVisitCompletedAt,
  resolveVisitTimes,
  visitDayLabel,
} from "../../lib/calloutSheet";
import {
  LATE_REASON_TEXT_MAX,
  calendarDaysBetween,
  lateReasonGiven,
  visitAgeBand,
  type LateReasonCode,
} from "../../lib/visitDateWindow";
import { FieldControl, NoAccessControl, TableField, TextBox } from "./projectSurvey/SurveyFields";
import { ExtraPhotos, PhotoSlot, type PhotoContext } from "./projectSurvey/SurveyPhotos";
import { SurveyPhotoNotices } from "./projectSurvey/SurveyPhotoNotices";

/** The autosave sends the cells changed since the last good save at most this often. */
export const SURVEY_AUTOSAVE_MS = 10_000;
const OFFICE_PHONE = "0203 576 4760";

type Screen = { name: "visit" } | { name: "survey"; surveyId: string } | { name: "section"; surveyId: string; sectionKey: string } | { name: "finish" };

const primary = "min-h-12 w-full rounded-xl border border-ele-green bg-ele-green px-4 py-2.5 font-heading text-base tracking-wide text-ele-bg disabled:opacity-40";
const quiet = "min-h-12 w-full rounded-xl border border-ele-card-border px-4 py-2.5 font-heading text-base tracking-wide text-ele-strong";
const chip = (active: boolean) =>
  `min-h-12 rounded-xl border px-3 py-2 text-left font-menu text-sm transition-colors ${
    active ? "border-ele-green bg-ele-green text-ele-bg" : "border-ele-card-border bg-ele-field text-ele-strong"
  }`;

/** The start of the visit day on this phone's clock: a survey worked on by an engineer since then counts as worked this visit. */
function startOfDayIso(ymd: string): string {
  const [y, m, d] = ymd.split("-").map(Number);
  return new Date(y!, (m ?? 1) - 1, d ?? 1).toISOString();
}

function sortSurveys(list: CachedVisitSurvey[]): CachedVisitSurvey[] {
  return [...list.filter((s) => s.kind === "site"), ...list.filter((s) => s.kind !== "site")];
}

export function ProjectSurveySheet({ jobId }: { jobId: string }) {
  const navigate = useNavigate();
  const { complete, online, jobById, syncNow } = useEngineerData();
  const job = jobById(jobId);

  const [ready, setReady] = useState(false);
  const [view, setView] = useState<ProjectSurveyVisitView | null>(null);
  const [loadNote, setLoadNote] = useState<string | null>(null);
  const [visit, setVisitState] = useState<SurveyVisitDraftRow>(() => emptyVisitDraft(jobId, new Date()));
  const [drafts, setDraftsState] = useState<Record<string, SurveyDraftRow>>({});
  const [queued, setQueued] = useState<QueuedPhoto[]>([]);
  const [screen, setScreen] = useState<Screen>({ name: "visit" });
  const [notices, setNotices] = useState<string[]>(() => readSurveyPhotoNotices());
  const [saveNote, setSaveNote] = useState<string | null>(null);
  const [newLabel, setNewLabel] = useState("");
  const [renaming, setRenaming] = useState<{ id: string; text: string; busy: boolean; error: string | null } | null>(null);
  const [acceptError, setAcceptError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [submitErrors, setSubmitErrors] = useState<string[]>([]);
  const [confirming, setConfirming] = useState(false);

  const visitRef = useRef(visit);
  const draftsRef = useRef(drafts);
  const onlineRef = useRef(online);
  onlineRef.current = online;
  const timerRef = useRef<number | null>(null);
  const flushingRef = useRef(false);
  const mountedRef = useRef(true);

  // ---- The device copy ----------------------------------------------------------------------------------------------
  const updateVisit = useCallback((fn: (cur: SurveyVisitDraftRow) => SurveyVisitDraftRow) => {
    const next = { ...fn(visitRef.current), savedAt: new Date().toISOString() };
    visitRef.current = next;
    if (mountedRef.current) setVisitState(next);
    void saveSurveyVisit(next);
  }, []);

  const refreshQueued = useCallback(async () => {
    try {
      const items = await queuedPhotosForJob(jobId);
      if (mountedRef.current) setQueued(items.filter((p) => p.target?.kind === "survey"));
    } catch {
      /* best effort */
    }
  }, [jobId]);

  // ---- The autosave ---------------------------------------------------------------------------------------------------
  const flush = useCallback(async () => {
    if (flushingRef.current || !onlineRef.current) return;
    flushingRef.current = true;
    try {
      // Lifts added on site first, each with the id this phone made, so a replay never creates two.
      for (const lift of visitRef.current.newLifts) {
        if (lift.created || lift.clash) continue;
        const type = draftsRef.current[lift.id]?.type?.value ?? lift.surveyType;
        try {
          await api.projectSurveys.addLift(jobId, { id: lift.id, label: lift.label, ...(type ? { surveyType: type } : {}) });
          updateVisit((cur) => ({ ...cur, newLifts: cur.newLifts.map((l) => (l.id === lift.id ? { ...l, created: true, clash: null } : l)) }));
          // The office holds this lift now: its queued photos are ordinary survey photos from here on (a later 404 means removed).
          void markSurveyLiftCreated(lift.id);
          // Session PJ2F review L2: a rename made while this add was in flight changed only the phone's copy, and the add carried the
          // old name. Now the office holds the lift, send the name the person settled on.
          const settled = visitRef.current.newLifts.find((l) => l.id === lift.id)?.label;
          if (settled && settled !== lift.label) {
            try {
              await api.projectSurveys.renameLift(jobId, lift.id, settled);
            } catch (renameErr) {
              if (renameErr instanceof ApiError && renameErr.status === 409) {
                const words = typeof renameErr.body?.message === "string" ? renameErr.body.message : "Another lift on this project already has that name.";
                updateVisit((cur) => ({ ...cur, newLifts: cur.newLifts.map((l) => (l.id === lift.id ? { ...l, clash: words } : l)) }));
              }
            }
          }
        } catch (err) {
          if (err instanceof ApiError && err.status === 409) {
            const words = typeof err.body?.message === "string" ? err.body.message : "Another lift on this project already has that name.";
            updateVisit((cur) => ({ ...cur, newLifts: cur.newLifts.map((l) => (l.id === lift.id ? { ...l, clash: words } : l)) }));
          } else {
            return; // no signal after all: try again on the next change or reconnect
          }
        }
      }
      for (const [surveyId, draft] of Object.entries(draftsRef.current)) {
        const lift = visitRef.current.newLifts.find((l) => l.id === surveyId);
        if (lift && !lift.created) continue;
        const body = pendingSave(draft);
        if (!body) continue;
        try {
          const res = await api.projectSurveys.saveRecord(surveyId, { ...body, jobId }, { background: true });
          const cur = draftsRef.current[surveyId] ?? draft;
          const next = afterSave(cur, body);
          draftsRef.current = { ...draftsRef.current, [surveyId]: next };
          if (mountedRef.current) setDraftsState(draftsRef.current);
          void saveSurveyDraft(next);
          const record = readRecord(res.record);
          updateVisit((v) => ({
            ...v,
            // The type the server kept (an office correction made after this phone's choice wins over it).
            surveys: v.surveys.map((s) => (s.id === surveyId ? { ...s, record, surveyType: isSurveyType(res.surveyType) ? res.surveyType : res.surveyType === null ? null : s.surveyType } : s)),
          }));
          if (mountedRef.current) setSaveNote(null);
        } catch (err) {
          if (err instanceof ApiError && err.status === 409) {
            // D-PJ2F2-2: the change is kept (it stays pending) and rides the visit's completion, which applies it and puts the survey
            // back to the office for review. A survey a resurvey REPLACED is skipped by the completion, so that is said instead.
            const superseded = err.body?.superseded === true;
            if (mountedRef.current) {
              setSaveNote(
                superseded
                  ? "The office has started a new survey of this lift, so changes made here cannot be sent. Ring the office if they are needed."
                  : "Saved on this phone. It will be sent with the visit for the office to review again.",
              );
            }
          } else if (err instanceof ApiError && err.status === 404) {
            if (mountedRef.current) setSaveNote("One of these surveys was removed by the office. Your changes are kept on this phone.");
          } else if (err instanceof ApiError && err.status === 403) {
            if (mountedRef.current) setSaveNote("This survey visit is not assigned to you any more, so changes are kept on this phone only. Ring the office.");
          } else if (err instanceof ApiError && err.status === 400) {
            // The server will never take these values as they are: sending them again every 10 seconds would change nothing. They stop
            // being re-sent (they stay on this phone, and on the visit's sheet) and the engineer is told (Session PJ2 review, HIGH 1).
            const cur = draftsRef.current[surveyId] ?? draft;
            const next = afterSave(cur, body);
            draftsRef.current = { ...draftsRef.current, [surveyId]: next };
            if (mountedRef.current) setDraftsState(draftsRef.current);
            void saveSurveyDraft(next);
            const words = typeof err.body?.message === "string" ? err.body.message : "Some of the survey values could not be accepted.";
            if (mountedRef.current) setSaveNote(`${words} They are kept on this phone. Ring the office.`);
          } else if (!(err instanceof ApiError)) {
            return; // no signal: the next change or reconnect tries again
          }
        }
      }
    } finally {
      flushingRef.current = false;
    }
  }, [jobId, updateVisit]);

  const scheduleAutosave = useCallback(() => {
    if (timerRef.current != null) return;
    timerRef.current = window.setTimeout(() => {
      timerRef.current = null;
      void flush();
    }, SURVEY_AUTOSAVE_MS);
  }, [flush]);

  const updateDraft = useCallback(
    (surveyId: string, fn: (cur: SurveyDraftRow) => SurveyDraftRow) => {
      const base = draftsRef.current[surveyId] ?? emptySurveyDraft(surveyId, jobId, new Date());
      let next = fn(base);
      // A changed type or number of floors: the cells that no longer apply leave the autosave (Session PJ2 review, HIGH 1).
      const floorsPath = fieldPath(FLOORS_COUNT_FIELD);
      if (next.type?.value !== base.type?.value || next.local[floorsPath] !== base.local[floorsPath]) {
        const survey = visitRef.current.surveys.find((x) => x.id === surveyId);
        const lift = visitRef.current.newLifts.find((l) => l.id === surveyId);
        const kind = survey?.kind ?? "lift";
        const serverType = survey?.surveyType ?? lift?.surveyType ?? null;
        const serverRecord = survey?.record ?? emptyRecord();
        next = pruneInapplicable(next, survey?.formVersion ?? SURVEY_FORM_VERSION, kind, effectiveType(serverType, next), effectiveRecord(serverRecord, next));
      }
      draftsRef.current = { ...draftsRef.current, [surveyId]: next };
      setDraftsState(draftsRef.current);
      void saveSurveyDraft(next);
      scheduleAutosave();
    },
    [jobId, scheduleAutosave],
  );

  const refreshView = useCallback(async () => {
    if (!onlineRef.current) {
      setLoadNote("No signal. Showing what is saved on this phone.");
      return;
    }
    try {
      const v = await api.projectSurveys.visit(jobId);
      if (!mountedRef.current) return;
      setView(v);
      setLoadNote(null);
      updateVisit((cur) => ({
        ...cur,
        projectRef: v.projectRef,
        surveys: sortSurveys(v.surveys.map(cachedSurveyFrom)),
        newLifts: cur.newLifts.map((l) => (v.surveys.some((s) => s.id === l.id) ? { ...l, created: true, clash: null } : l)),
      }));
      for (const s of v.surveys) if (s.addedOnSite) void markSurveyLiftCreated(s.id);
    } catch {
      if (mountedRef.current) setLoadNote("Could not reach the office. Showing what is saved on this phone.");
    }
  }, [jobId, updateVisit]);

  // Open: this phone's copy first (instant, offline), then the server's, merged per path.
  useEffect(() => {
    mountedRef.current = true;
    let live = true;
    void (async () => {
      const local = await loadSurveyVisit(jobId);
      if (!live) return;
      const v = local.visit ?? emptyVisitDraft(jobId, new Date());
      const d: Record<string, SurveyDraftRow> = {};
      for (const row of local.surveys) d[row.surveyId] = row;
      draftsRef.current = d;
      visitRef.current = v;
      setDraftsState(d);
      setVisitState(v);
      setReady(true);
      await refreshView();
      await refreshQueued();
    })();
    return () => {
      live = false;
      mountedRef.current = false;
      if (timerRef.current != null) window.clearTimeout(timerRef.current);
      timerRef.current = null;
      // Leaving the sheet with signal: one last save. Everything is on the device either way.
      void flush();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);

  // Signal back: send what waited.
  useEffect(() => {
    if (online && ready) void flush();
  }, [online, ready, flush]);

  useEffect(() => {
    const onNotices = () => setNotices(readSurveyPhotoNotices());
    window.addEventListener(SURVEY_NOTICES_EVENT, onNotices);
    return () => window.removeEventListener(SURVEY_NOTICES_EVENT, onNotices);
  }, []);

  // ---- The visit day (the job sheet's own rules, lib/calloutSheet.ts and lib/visitDateWindow.ts) --------------------
  const todayYmd = localYmd(new Date());
  const bookedYmd = isoToLocalYmd(job?.scheduledFor ?? null);
  const visitDayUnchosen = !visit.visitDateYmd && !!bookedYmd && bookedYmd < todayYmd;
  const visitYmd = visit.visitDateYmd || (visitDayUnchosen ? todayYmd : defaultVisitYmd(job?.scheduledFor ?? null, new Date()));
  const pastVisit = visitYmd < todayYmd;
  const visitBand = visitAgeBand(calendarDaysBetween(visitYmd, todayYmd));
  const lateReasonMissing = !visitDayUnchosen && visitBand === "late" && !lateReasonGiven(visit.lateReason, visit.lateReasonText);
  const sinceIso = startOfDayIso(visitYmd);

  // ---- The surveys on this visit ----------------------------------------------------------------------------------------
  const surveys: CachedVisitSurvey[] = useMemo(() => {
    const list = [...visit.surveys];
    for (const l of visit.newLifts) {
      if (list.some((s) => s.id === l.id)) continue;
      list.push({ id: l.id, kind: "lift", label: l.label, surveyType: l.surveyType, addedOnSite: true, formVersion: SURVEY_FORM_VERSION, record: emptyRecord(), submittedAt: null, reviewedAt: null, photos: [] });
    }
    return sortSurveys(list);
  }, [visit.surveys, visit.newLifts]);

  const states: VisitSurveyState[] = useMemo(
    () =>
      surveys.map((s) => ({
        id: s.id,
        kind: s.kind,
        label: s.kind === "site" ? SITE_LABEL : s.label ?? "Lift",
        formVersion: s.formVersion,
        serverType: s.surveyType,
        serverRecord: s.record,
        reviewed: !!s.reviewedAt,
        draft: drafts[s.id],
        outcome: visit.outcomes[s.id],
      })),
    [surveys, drafts, visit.outcomes],
  );

  const photoCtx = (s: VisitSurveyState): PhotoContext => ({
    jobId,
    surveyId: s.id,
    surveyLabel: s.label,
    newLift: visit.newLifts.some((l) => l.id === s.id && !l.created),
    onChanged: () => {
      void refreshQueued();
      if (onlineRef.current) void syncNow().then(() => refreshQueued());
    },
  });

  if (!job) {
    return (
      <div className="pb-8">
        <BackLink />
        <p className="mt-4 text-ele-body" role="status">This visit is not on this phone.</p>
      </div>
    );
  }
  if (!ready) {
    return (
      <div className="pb-8">
        <BackLink />
        <p className="mt-4 text-ele-body" role="status">Loading the survey...</p>
      </div>
    );
  }

  const siteName = view?.siteName ?? job.siteName;
  const siteAddress = view?.siteAddress ?? job.siteAddress;
  const accessNote = view?.accessNote ?? job.description;
  const projectRef = view?.projectRef ?? visit.projectRef;

  const header = (
    <header className="mb-4">
      <BackLink />
      <p className="mt-2 inline-flex items-center gap-1 rounded-full border border-ele-card-border px-2 py-0.5 font-menu text-xs uppercase tracking-wide text-ele-body">
        <Ruler className="h-3.5 w-3.5" aria-hidden="true" /> Project survey
      </p>
      <h1 className="mt-2 font-heading text-[26px] font-bold leading-tight text-ele-strong">{projectRef ?? "Project survey"}</h1>
      <p className="text-sm text-ele-strong">{siteName}</p>
      {siteAddress && <p className="text-sm text-ele-muted">{siteAddress}</p>}
      {loadNote && <p role="status" className="mt-2 text-sm" style={{ color: "var(--ele-amber-text)" }}>{loadNote}</p>}
      {saveNote && <p role="alert" className="mt-2 text-sm" style={{ color: "var(--ele-amber-text)" }}>{saveNote}</p>}
      <SurveyPhotoNotices notices={notices} />
    </header>
  );

  // ---- Crew (D-PJ2-2): read only ----------------------------------------------------------------------------------------
  if (job.crewMember) {
    return (
      <div className="pb-8">
        {header}
        <section className="rounded-ele border border-ele-blue bg-ele-card p-5">
          <h2 className="font-heading text-lg text-ele-strong">You are on this survey as crew</h2>
          <p className="mt-1 text-sm text-ele-body">
            {job.leadEngineerName ? `${job.leadEngineerName} is leading this visit and keeps the survey.` : "Another engineer is leading this visit and keeps the survey."} Read the RAMS before you attend.
          </p>
          {job.ramsDoc && <RamsLink jobId={jobId} code={job.ramsDoc.code} label={job.ramsDoc.label} />}
        </section>
      </div>
    );
  }

  // ---- Sent: the visit is in the outbox or with the office --------------------------------------------------------------
  if (job.pendingSync || job.status === "complete" || job.status === "pending_approval") {
    return (
      <div className="pb-8">
        {header}
        {job.syncEscalated ? (
          <div role="alert" className="rounded-ele border border-amber-500 bg-amber-500/10 p-5">
            <p className="font-heading text-lg text-ele-strong">This survey visit could not sync automatically</p>
            <p className="mt-1 text-sm text-ele-strong">{job.syncEscalatedReason}</p>
            <p className="mt-2 text-sm text-ele-body">Your work is saved on this device and has not been lost. Please contact the office on {OFFICE_PHONE}.</p>
            <button type="button" className={`${quiet} mt-3`} onClick={() => void retryEscalated(jobId).then(() => syncNow())}>
              Try again
            </button>
          </div>
        ) : (
          <p className="rounded-ele border border-ele-card-border bg-ele-card p-5 text-ele-body" role="status">
            {job.pendingSync ? "Survey submitted. Saved on this phone, it sends when you have signal." : "Survey submitted. The office has it."}
          </p>
        )}
      </div>
    );
  }

  // ---- Acceptance and the RAMS: one tap, exactly as on every other job ----------------------------------------------
  const accepted = !!job.acceptedAt || visit.acceptedLocal || job.status === "in_progress";
  const mine = job.assignedToMe !== false;
  const ramsToConfirm = mine ? (job.ramsDoc ?? null) : null;
  const ramsAcknowledged = !!job.ramsAcknowledgedAt || visit.ramsAckLocal;

  async function acceptJob() {
    if (accepted || busy) return;
    const confirmsRams = ramsToConfirm !== null;
    updateVisit((cur) => ({ ...cur, acceptedLocal: true, ramsAckLocal: cur.ramsAckLocal || confirmsRams }));
    setAcceptError(null);
    if (!onlineRef.current) return;
    setBusy(true);
    try {
      if (confirmsRams) await api.jobs.accept(jobId, { ramsAcknowledged: true });
      else await api.jobs.accept(jobId);
      await syncNow();
    } catch (err) {
      const status = (err as { status?: unknown } | null)?.status;
      const body = (err as { body?: { message?: unknown } } | null)?.body;
      if (status === 403) {
        updateVisit((cur) => ({ ...cur, acceptedLocal: false, ramsAckLocal: false }));
        setAcceptError(typeof body?.message === "string" ? body.message : "This job is not yours to accept.");
      } else {
        setAcceptError("Saved on this device. It will record when you are back online.");
      }
    } finally {
      setBusy(false);
    }
  }

  async function declineJob() {
    if (busy) return;
    if (!onlineRef.current) {
      setAcceptError(`You need a connection to decline a job. Please call the office on ${OFFICE_PHONE}.`);
      return;
    }
    setBusy(true);
    try {
      await api.jobs.decline(jobId);
      await clearSurveyVisit(jobId);
      await syncNow();
      navigate("/engineer", { replace: true });
    } catch {
      setAcceptError("That could not be sent. Check your signal and try again, or call the office.");
      setBusy(false);
    }
  }

  async function acknowledgeRams() {
    if (ramsAcknowledged) return;
    updateVisit((cur) => ({ ...cur, ramsAckLocal: true }));
    if (!onlineRef.current) return;
    try {
      await api.jobs.acknowledgeRams(jobId);
      await syncNow();
    } catch {
      /* the completion carries it */
    }
  }

  if (!accepted) {
    return (
      <div className="pb-8">
        {header}
        {accessNote && <AccessNote text={accessNote} />}
        <section className="rounded-ele border border-ele-purple bg-ele-card p-5">
          <h2 className="font-heading text-lg text-ele-strong">{mine ? "Accept this job?" : "Waiting to be accepted"}</h2>
          <p className="mt-1 text-sm text-ele-body">
            {mine
              ? "The office has put this survey on your schedule. Accept it to open the survey, or decline it to send it straight back to them."
              : "The engineer this job is assigned to has not accepted it yet. You cannot accept it for them."}
          </p>
          {ramsToConfirm && (
            <div className="mt-3 rounded-ele border border-ele-card-border bg-ele-inset p-3">
              <RamsLink jobId={jobId} code={ramsToConfirm.code} label={ramsToConfirm.label} />
              <p id="accept-rams-statement" className="mt-2 text-sm text-ele-strong">
                By accepting, you confirm you have read and understood the applicable RAMS for this job.
              </p>
            </div>
          )}
          {acceptError && <p role="alert" className="mt-3 text-sm text-[var(--ele-amber-text-deep)]">{acceptError}</p>}
          <div className="mt-4 flex flex-col gap-2">
            {mine && (
              <button type="button" disabled={busy} onClick={() => void acceptJob()} aria-describedby={ramsToConfirm ? "accept-rams-statement" : undefined} className={primary}>
                {ramsToConfirm ? "Accept and confirm RAMS" : "Accept job"}
              </button>
            )}
            <button type="button" className={quiet} disabled={busy} onClick={() => void declineJob()}>
              Decline
            </button>
          </div>
        </section>
      </div>
    );
  }

  // ---- Changes ------------------------------------------------------------------------------------------------------------
  const setCellFor = (surveyId: string) => (path: string, cell: SurveyCell) => updateDraft(surveyId, (d) => withCell(d, path, cell));
  const timeCard = (
    <TimeOnSiteCard
      arrival={visit.arrival}
      departure={visit.departure}
      onArrival={(v) => updateVisit((c) => ({ ...c, arrival: v }))}
      onDeparture={(v) => updateVisit((c) => ({ ...c, departure: v }))}
      visitYmd={visitYmd}
      todayYmd={todayYmd}
      bookedYmd={bookedYmd}
      visitDayChosen={!visitDayUnchosen}
      onVisitDate={(ymd) => updateVisit((c) => ({ ...c, visitDateYmd: ymd }))}
      lateReason={visit.lateReason}
      lateReasonText={visit.lateReasonText}
      onLateReason={(code) => updateVisit((c) => ({ ...c, lateReason: code }))}
      onLateReasonText={(t) => updateVisit((c) => ({ ...c, lateReasonText: t }))}
    />
  );

  const ramsCard = !ramsAcknowledged ? (
    <section className="mb-4 rounded-lg border border-[#b8860b] bg-ele-card p-4">
      <p className="font-heading text-base text-ele-strong">Compliance: RAMS</p>
      {job.ramsDoc ? <RamsLink jobId={jobId} code={job.ramsDoc.code} label={job.ramsDoc.label} /> : <p className="text-sm text-ele-body">The RAMS will be available when this job next syncs.</p>}
      <button type="button" className={`${quiet} mt-2`} onClick={() => void acknowledgeRams()}>
        I have read the RAMS
      </button>
    </section>
  ) : null;

  // Session PJ2F: rename a lift THIS phone added on site. Before the office holds it, only the phone's copy changes (the add request
  // carries the new name); once it does, the office is asked (a name another lift has is refused and said in words).
  async function saveRename() {
    if (!renaming || renaming.busy) return;
    const label = renaming.text.trim();
    const id = renaming.id;
    if (!label) return;
    const apply = () =>
      updateVisit((cur) => ({
        ...cur,
        newLifts: cur.newLifts.map((l) => (l.id === id ? { ...l, label, clash: null } : l)),
        surveys: cur.surveys.map((s) => (s.id === id ? { ...s, label } : s)),
      }));
    const lift = visitRef.current.newLifts.find((l) => l.id === id);
    if (!lift?.created) {
      apply();
      setRenaming(null);
      void flush();
      return;
    }
    if (!onlineRef.current) {
      setRenaming({ ...renaming, error: "No signal. Try again when you have signal." });
      return;
    }
    setRenaming({ ...renaming, busy: true, error: null });
    try {
      await api.projectSurveys.renameLift(jobId, id, label);
      apply();
      if (mountedRef.current) setRenaming(null);
    } catch (err) {
      const words =
        err instanceof ApiError && typeof err.body?.message === "string"
          ? err.body.message
          : "Could not rename it just now. Try again with signal.";
      if (mountedRef.current) setRenaming({ id, text: renaming.text, busy: false, error: words });
    }
  }

  function addLift() {
    const label = newLabel.trim();
    if (!label) return;
    const id = phoneMadeId();
    updateVisit((cur) => ({ ...cur, newLifts: [...cur.newLifts, { id, label, surveyType: null, created: false, clash: null }] }));
    setNewLabel("");
    void flush();
  }

  // ---- A section ------------------------------------------------------------------------------------------------------------
  if (screen.name === "section") {
    const s = states.find((x) => x.id === screen.surveyId);
    const section = s ? findSection(s.formVersion, screen.sectionKey) : null;
    if (!s || !section) return (
      <div className="pb-8">
        {header}
        <p className="text-ele-body">That survey is not on this visit any more.</p>
        <button type="button" className={`${quiet} mt-4`} onClick={() => setScreen({ name: "visit" })}>
          Back to the visit
        </button>
      </div>
    );
    const type = effectiveType(s.serverType, s.draft);
    const record = effectiveRecord(s.serverRecord, s.draft);
    const setCell = setCellFor(s.id);
    const sectionNa = record.cells[sectionPath(section.key)];
    const fields = visibleFields(s.formVersion, section, type, record);
    const ctx = photoCtx(s);
    const surveyPhotos = { server: surveys.find((x) => x.id === s.id)?.photos ?? [], queued: queued.filter((q) => q.target?.surveyId === s.id) };
    const goNext = () => {
      const next = nextSectionWithWork(s.formVersion, s.kind, type, record, section.key);
      setScreen(next ? { name: "section", surveyId: s.id, sectionKey: next } : { name: "survey", surveyId: s.id });
      if (typeof window.scrollTo === "function") window.scrollTo(0, 0);
    };
    const notes = valueAt(record, notesPath(section.key));
    return (
      <div className="pb-28">
        {header}
        <p className="font-menu text-xs uppercase tracking-wide text-ele-muted">{s.label}</p>
        <h2 className="mt-1 font-heading text-xl text-ele-strong">{section.title}</h2>
        {section.hint && <p className="mb-2 text-sm text-ele-muted">{section.hint}</p>}
        <NoAccessControl
          what={section.title}
          cell={sectionNa}
          onMark={(reason, note) => setCell(sectionPath(section.key), noAccessCell(reason, note, new Date()))}
          onClear={() => setCell(sectionPath(section.key), valueCell(null, new Date()))}
        />
        {!sectionNa?.na && (
          <div className="mt-3">
            {fields.map((f) => {
              if (f.kind === "table") return <TableField key={f.key} field={f} record={record} setCell={setCell} />;
              if (f.kind === "photo") {
                // A reviewed survey takes no photo from the phone (the server refuses it), so its photo slots are not offered (PJ2F2 review).
                if (s.reviewed) return null;
                return photoSlotKeys(f, record).map((slot) => <PhotoSlot key={slot.slotKey} ctx={ctx} slotKey={slot.slotKey} label={slot.label} photos={surveyPhotos} />);
              }
              return <FieldControl key={f.key} field={f} record={record} setCell={setCell} notesKey={notesPath(section.key)} />;
            })}
          </div>
        )}
        <div className="mb-4 rounded-xl border border-ele-card-border bg-ele-card p-3">
          <TextBox
            label={`Notes for ${section.title}`}
            hint="You can dictate"
            multiline
            value={notes}
            onChange={(v) => setCell(notesPath(section.key), valueCell(v, new Date()))}
          />
        </div>
        <div className="mt-5 grid grid-cols-2 gap-2">
          <button type="button" className={quiet} onClick={() => setScreen({ name: "survey", surveyId: s.id })}>
            Back
          </button>
          <button type="button" className={primary} onClick={goNext}>
            Next
          </button>
        </div>
      </div>
    );
  }

  // ---- One survey (the site, or a lift) ----------------------------------------------------------------------------------
  if (screen.name === "survey") {
    const s = states.find((x) => x.id === screen.surveyId);
    if (!s) return (
      <div className="pb-8">
        {header}
        <p className="text-ele-body">That survey is not on this visit any more.</p>
        <button type="button" className={`${quiet} mt-4`} onClick={() => setScreen({ name: "visit" })}>
          Back to the visit
        </button>
      </div>
    );
    const type = effectiveType(s.serverType, s.draft);
    const record = effectiveRecord(s.serverRecord, s.draft);
    const sections = visibleSections(s.formVersion, s.kind, type);
    const outcome = visit.outcomes[s.id];
    const setOutcome = (skip: boolean, reason: NotSurveyedReason | null) =>
      updateVisit((cur) => ({ ...cur, outcomes: { ...cur.outcomes, [s.id]: { skip, reason } } }));
    return (
      <div className="pb-28">
        {header}
        <h2 className="font-heading text-xl text-ele-strong">{s.label}</h2>
        {/* D-PJ2F2-3: a reviewed survey's editor stays open on a later visit; what is changed rides the visit and goes back for review. */}
        {s.reviewed && (
          <p className="mt-1 text-sm text-ele-muted" data-reviewed-note>
            The office has reviewed {s.kind === "site" ? "this survey" : "this lift"}. Anything you change will be sent for them to review again.
          </p>
        )}
        {s.reviewed && (
          <p className="mt-1 text-sm text-ele-muted">Photos cannot be added to it from the phone now. Ring the office if one is needed.</p>
        )}
        {s.kind === "lift" && (
          <fieldset className="mt-3">
            <legend className="font-heading text-lg text-ele-strong">What are you surveying?</legend>
            <div className="mt-2 grid grid-cols-1 gap-2">
              {SURVEY_TYPE_CODES.map((t) => (
                <button
                  key={t}
                  type="button"
                  aria-pressed={type === t}
                  className={`${chip(type === t)} min-h-14 text-base`}
                  onClick={() => updateDraft(s.id, (d) => withType(d, t as SurveyType, new Date()))}
                >
                  {SURVEY_TYPES[t]}
                </button>
              ))}
            </div>
          </fieldset>
        )}
        <>
            <h3 className="mt-5 font-heading text-base text-ele-strong">Sections</h3>
            <ul className="mt-2 space-y-2">
              {sections.map((sec) => {
                const st = sectionState(s.formVersion, sec, type, record);
                const word = { done: "Done", started: "Started", not_started: "Not started", no_access: "Could not access" }[st];
                return (
                  <li key={sec.key}>
                    <button
                      type="button"
                      onClick={() => setScreen({ name: "section", surveyId: s.id, sectionKey: sec.key })}
                      className="flex min-h-14 w-full items-center justify-between gap-2 rounded-xl border border-ele-card-border bg-ele-card px-3 py-2 text-left"
                    >
                      <span className="min-w-0 font-heading text-base text-ele-strong">{sec.title}</span>
                      <span className="flex shrink-0 items-center gap-1 text-sm text-ele-muted">
                        {word} <ChevronRight className="h-4 w-4" aria-hidden="true" />
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
            <div className="mt-4 rounded-xl border border-ele-card-border bg-ele-card p-3">
              <TextBox
                label="Notes for the whole survey"
                hint="You can dictate"
                multiline
                value={valueAt(record, SURVEY_NOTES_PATH)}
                onChange={(v) => updateDraft(s.id, (d) => withCell(d, SURVEY_NOTES_PATH, valueCell(v, new Date())))}
              />
            </div>
            {!s.reviewed && <ExtraPhotos ctx={photoCtx(s)} photos={{ server: surveys.find((x) => x.id === s.id)?.photos ?? [], queued: queued.filter((q) => q.target?.surveyId === s.id) }} />}
            {/* "Could not survey" means nothing for a survey the office has already reviewed: it is left out of that question. */}
            {!s.reviewed && (
              <div className="mt-4 rounded-xl border border-ele-card-border bg-ele-card p-3">
                {outcome?.skip ? (
                  <>
                    <p className="text-sm text-ele-strong">Not surveyed this visit{outcome.reason ? `: ${NOT_SURVEYED_REASONS[outcome.reason]}` : ""}. It stays open for another visit.</p>
                    <ReasonChips value={outcome.reason} onPick={(r) => setOutcome(true, r)} />
                    <button type="button" className={`${quiet} mt-2`} onClick={() => setOutcome(false, null)}>
                      I am surveying it after all
                    </button>
                  </>
                ) : (
                  <button type="button" className="min-h-11 text-sm text-ele-muted underline" onClick={() => setOutcome(true, null)}>
                    Could not survey {s.kind === "site" ? "the site" : "this lift"} this visit
                  </button>
                )}
              </div>
            )}
        </>
        <button type="button" className={`${quiet} mt-5`} onClick={() => setScreen({ name: "visit" })}>
          Back to the visit
        </button>
      </div>
    );
  }

  // ---- Finish: what is left, per survey, then one submit for the visit -----------------------------------------------
  const plans = states.map((s) => planSurvey(s, sinceIso));

  function visitProblems(at: Date): string[] {
    const out: string[] = [];
    for (const p of plans) out.push(...p.blockers);
    for (const l of visit.newLifts) if (l.clash) out.push(`${l.label}: ${l.clash} Rename it on the visit screen.`);
    if (!ramsAcknowledged) out.push("Confirm you have read the applicable RAMS before submitting.");
    if (visit.arrival && visit.departure && visit.departure <= visit.arrival) out.push("The Left time must be after the Arrived time. Check both times.");
    if (visitDayUnchosen) out.push("Tap the day you did this job.");
    else if (visitYmd > localYmd(at)) out.push("The visit date cannot be in the future.");
    else if (visitBand === "too_old") out.push("That visit is more than 2 weeks ago. Contact the office, who will record it.");
    else if (lateReasonMissing) out.push("Choose why this visit is being sent late.");
    else if (pastVisit && (!visit.arrival || !visit.departure)) out.push(`For a visit on ${visitDayLabel(visitYmd)}, enter the time you arrived and the time you left.`);
    return out;
  }

  async function submit() {
    if (busy) return;
    const at = new Date();
    const problems = visitProblems(at);
    if (problems.length) {
      setSubmitErrors(problems);
      setConfirming(false);
      return;
    }
    setBusy(true);
    try {
      const times = resolveVisitTimes({
        visitYmd,
        nowIso: at.toISOString(),
        openedAtIso: visit.openedAt,
        arrivalHHMM: visit.arrival,
        departureHHMM: visit.departure,
        arrivalStampIso: null,
        departureStampIso: null,
        // A survey is an observational visit: a blank Arrived falls back to when the visit was opened, as on a service visit.
        fallBackToOpenAndNow: true,
        crossesMidnight: false,
        allowNowDefaults: !pastVisit,
      });
      const visitStamp = pastVisit
        ? {
            completedAt: pastVisitCompletedAt(visitYmd, times.departureISO, at.toISOString()),
            visitDate: visitYmd,
            submittedAt: at.toISOString(),
            ...(visitBand === "late" && lateReasonGiven(visit.lateReason, visit.lateReasonText)
              ? {
                  lateReason: visit.lateReason as LateReasonCode,
                  ...(visit.lateReason === "other" ? { lateReasonText: visit.lateReasonText.trim().slice(0, LATE_REASON_TEXT_MAX) } : {}),
                }
              : {}),
          }
        : { completedAt: at.toISOString() };
      await complete(jobId, {
        checklist: [],
        defects: [],
        partsUsed: [],
        projectSurvey: buildProjectSurveyPayload(states, visit.newLifts, sinceIso),
        ...(times.arrivalISO ? { arrivalTime: times.arrivalISO } : {}),
        ...(times.departureISO ? { departureTime: times.departureISO } : {}),
        ...visitStamp,
        ramsAcknowledged,
        accepted: true,
      });
      await clearSurveyVisit(jobId);
      navigate("/engineer", { replace: true, state: { submitted: { ref: projectRef ?? "Project survey", site: siteName ?? "", online: onlineRef.current } } });
    } catch {
      setSubmitErrors(["Could not submit. Your survey is saved on this phone."]);
      setBusy(false);
    }
  }

  if (screen.name === "finish") {
    const slotsTaken = (s: VisitSurveyState) =>
      new Set([...(surveys.find((x) => x.id === s.id)?.photos ?? []).map((p) => p.slotKey), ...queued.filter((q) => q.target?.surveyId === s.id).map((q) => q.target!.slotKey)]);
    const photosToSend = queued.filter((q) => q.status !== "escalated").length;
    return (
      <div className="pb-28">
        {header}
        <h2 className="font-heading text-xl text-ele-strong">Finish the visit</h2>
        <div className="mt-3">{timeCard}</div>
        {ramsCard}
        <ul className="space-y-3">
          {plans.map((p) => {
            const s = states.find((x) => x.id === p.id)!;
            if (p.reviewed && !p.reviewChanged) {
              return (
                <li key={p.id} className="rounded-xl border border-ele-card-border bg-ele-card p-3">
                  <p className="font-heading text-base text-ele-strong">{p.label}</p>
                  <p className="text-sm text-ele-muted">Reviewed by the office.</p>
                </li>
              );
            }
            if (p.reviewChanged) {
              return (
                <li key={p.id} className="rounded-xl border border-ele-card-border bg-ele-card p-3" data-survey-plan={p.id}>
                  <p className="font-heading text-base text-ele-strong">{p.label}</p>
                  <p className="text-sm text-ele-body">Changed after the office reviewed it. Your changes go with this visit for them to review again.</p>
                  {p.blockers.length > 0 && (
                    <ul className="mt-2 list-disc pl-5 text-sm text-ele-danger">
                      {p.blockers.map((b) => (
                        <li key={b}>{b}</li>
                      ))}
                    </ul>
                  )}
                </li>
              );
            }
            const type = effectiveType(s.serverType, s.draft);
            const record = effectiveRecord(s.serverRecord, s.draft);
            const gaps = p.outcome === "surveyed" ? surveyGaps(s.formVersion, s.kind, type, record, slotsTaken(s)) : [];
            const setReason = (r: NotSurveyedReason) =>
              updateVisit((cur) => ({ ...cur, outcomes: { ...cur.outcomes, [p.id]: { skip: !p.untouched, reason: r } } }));
            return (
              <li key={p.id} className="rounded-xl border border-ele-card-border bg-ele-card p-3" data-survey-plan={p.id}>
                <p className="font-heading text-base text-ele-strong">{p.label}</p>
                {p.outcome === "surveyed" ? (
                  <>
                    <p className="text-sm text-ele-body">Surveyed this visit{type ? `: ${SURVEY_TYPES[type]}` : ""}.</p>
                    {gaps.length > 0 ? (
                      <details className="mt-2">
                        <summary className="min-h-11 cursor-pointer py-2 text-sm text-ele-strong">
                          {gaps.length} section{gaps.length === 1 ? "" : "s"} with something left blank or not accessible
                        </summary>
                        <ul className="mt-1 space-y-2 text-sm text-ele-body">
                          {gaps.map((g) => (
                            <li key={g.section}>
                              <span className="font-semibold text-ele-strong">{g.section}</span>
                              {g.noAccess && ": could not access the section"}
                              {g.blank.length > 0 && <span className="block">Left blank: {g.blank.join("; ")}</span>}
                              {g.notAccessible.length > 0 && <span className="block">Could not access: {g.notAccessible.join("; ")}</span>}
                              {g.photosMissing > 0 && <span className="block">Photos not taken: {g.photosMissing}</span>}
                            </li>
                          ))}
                        </ul>
                      </details>
                    ) : (
                      <p className="text-sm text-ele-muted">Nothing left blank.</p>
                    )}
                  </>
                ) : (
                  <>
                    <p className="text-sm text-ele-body">Not surveyed this visit. It stays open for another visit. Why?</p>
                    <ReasonChips value={p.reason} onPick={setReason} />
                  </>
                )}
                {p.blockers.length > 0 && (
                  <ul className="mt-2 list-disc pl-5 text-sm text-ele-danger">
                    {p.blockers.map((b) => (
                      <li key={b}>{b}</li>
                    ))}
                  </ul>
                )}
              </li>
            );
          })}
        </ul>
        {photosToSend > 0 && <p className="mt-3 text-sm text-ele-muted">{photosToSend} photo{photosToSend === 1 ? "" : "s"} still to send. They go on their own when there is signal.</p>}
        {submitErrors.length > 0 && (
          <ul role="alert" className="mt-4 list-disc space-y-1 pl-5 text-sm text-ele-danger">
            {submitErrors.map((e) => (
              <li key={e}>{e}</li>
            ))}
          </ul>
        )}
        {confirming ? (
          <div className="mt-5 rounded-xl border border-ele-card-border bg-ele-card p-4">
            <p className="font-heading text-base text-ele-strong">Submit the survey visit?</p>
            <p className="mt-1 text-sm text-ele-body">
              Visit on {visitDayLabel(visitYmd)}
              {visitYmd === todayYmd ? " (today)" : ""}. {plans.filter((p) => (!p.reviewed || p.reviewChanged) && p.outcome === "surveyed").length} surveyed,{" "}
              {plans.filter((p) => !p.reviewed && p.outcome === "not_surveyed").length} left for another visit.
            </p>
            <div className="mt-3 grid grid-cols-2 gap-2">
              <button type="button" className={quiet} onClick={() => setConfirming(false)}>
                Back
              </button>
              <button type="button" className={primary} disabled={busy} onClick={() => void submit()}>
                Yes, submit
              </button>
            </div>
          </div>
        ) : (
          <div className="mt-5 grid grid-cols-2 gap-2">
            <button type="button" className={quiet} onClick={() => setScreen({ name: "visit" })}>
              Back
            </button>
            <button
              type="button"
              className={primary}
              onClick={() => {
                const problems = visitProblems(new Date());
                setSubmitErrors(problems);
                if (problems.length === 0) setConfirming(true);
              }}
            >
              Submit
            </button>
          </div>
        )}
      </div>
    );
  }

  // ---- The visit screen ---------------------------------------------------------------------------------------------------
  return (
    <div className="pb-28">
      {header}
      {accessNote && <AccessNote text={accessNote} />}
      {ramsCard}
      {timeCard}
      <h2 className="font-heading text-lg text-ele-strong">This visit</h2>
      {states.length === 0 && (
        <p className="mt-2 text-sm text-ele-body" role="status">
          The survey forms for this visit are not on this phone yet. Open this visit once with signal and they are kept here for the
          rest of the visit.
        </p>
      )}
      <ul className="mt-2 space-y-2">
        {states.map((s) => {
          const type = effectiveType(s.serverType, s.draft);
          const record = effectiveRecord(s.serverRecord, s.draft);
          const prog = surveyProgress(s.formVersion, s.kind, type, record);
          const outcome = visit.outcomes[s.id];
          const touched = prog.done > 0 || !!type || Object.keys(record.cells).length > 0;
          const progressWord = s.reviewed
            ? reviewedSurveyChanged(s)
              ? "Changed since the office reviewed it"
              : "Reviewed by the office"
            : (outcome?.skip && outcome.reason === "no_access") || prog.allNoAccess
              ? "Could not access"
              : outcome?.skip
                ? "Not surveyed this visit"
                : touched
                  ? `${prog.done} of ${prog.total} done`
                  : "Not started";
          const lift = visit.newLifts.find((l) => l.id === s.id);
          return (
            <li key={s.id}>
              <button
                type="button"
                onClick={() => setScreen({ name: "survey", surveyId: s.id })}
                className="flex min-h-14 w-full items-center justify-between gap-2 rounded-xl border border-ele-card-border bg-ele-card px-3 py-2 text-left"
              >
                <span className="min-w-0">
                  <span className="block font-heading text-base text-ele-strong">{s.label}</span>
                  {s.kind === "lift" && <span className="block text-xs text-ele-muted">{type ? SURVEY_TYPES[type] : "Type not chosen yet"}</span>}
                </span>
                <span className="flex shrink-0 items-center gap-1 text-sm text-ele-muted">
                  {progressWord} <ChevronRight className="h-4 w-4" aria-hidden="true" />
                </span>
              </button>
              {lift && !s.reviewed && !lift.clash && (
                renaming?.id === s.id ? (
                  <div className="mt-1 rounded-xl border border-ele-card-border p-2">
                    <TextBox label={`New name for ${s.label}`} value={renaming.text} onChange={(v) => setRenaming({ ...renaming, text: v ?? "", error: null })} />
                    {renaming.error && <p role="alert" className="mt-1 text-sm text-ele-danger">{renaming.error}</p>}
                    <div className="mt-2 grid grid-cols-2 gap-2">
                      <button type="button" className={quiet} onClick={() => setRenaming(null)}>
                        Cancel
                      </button>
                      <button type="button" className={primary} disabled={!renaming.text.trim() || renaming.busy} onClick={() => void saveRename()}>
                        Save name
                      </button>
                    </div>
                  </div>
                ) : (
                  <button type="button" className="mt-1 min-h-11 text-sm text-ele-muted underline" onClick={() => setRenaming({ id: s.id, text: s.label, busy: false, error: null })}>
                    Rename {s.label}
                  </button>
                )
              )}
              {lift?.clash && (
                <div role="alert" className="mt-1 rounded-xl border border-ele-card-border p-2 text-sm text-ele-strong" style={{ backgroundColor: "var(--ele-pill-tint-amber)" }}>
                  {lift.clash}
                  <TextBox
                    label={`New name for ${lift.label}`}
                    value={lift.label}
                    onChange={(v) => updateVisit((cur) => ({ ...cur, newLifts: cur.newLifts.map((l) => (l.id === lift.id ? { ...l, label: v ?? "" } : l)) }))}
                  />
                  <button
                    type="button"
                    className={`${quiet} mt-2`}
                    disabled={!lift.label.trim()}
                    onClick={() => {
                      updateVisit((cur) => ({ ...cur, newLifts: cur.newLifts.map((l) => (l.id === lift.id ? { ...l, label: l.label.trim(), clash: null } : l)) }));
                      void flush();
                    }}
                  >
                    Use this name
                  </button>
                </div>
              )}
            </li>
          );
        })}
      </ul>
      <div className="mt-4 rounded-xl border border-ele-card-border bg-ele-card p-3">
        <TextBox label="Add another lift here" hint="Its name, for example Lift 3. No customer names." value={newLabel} onChange={(v) => setNewLabel(v ?? "")} />
        <button type="button" className={`${quiet} mt-2`} disabled={!newLabel.trim()} onClick={addLift}>
          Add lift
        </button>
      </div>
      <button type="button" className={`${primary} mt-5`} onClick={() => setScreen({ name: "finish" })}>
        Finish the visit
      </button>
    </div>
  );
}

function BackLink() {
  return (
    <Link to="/engineer" className="inline-flex min-h-11 items-center gap-1 text-sm text-ele-muted">
      <ChevronLeft className="h-4 w-4" aria-hidden="true" /> Back
    </Link>
  );
}

function AccessNote({ text }: { text: string }) {
  return (
    <section className="mb-4 rounded-xl border border-ele-card-border bg-ele-card p-3">
      <h2 className="font-menu text-[11px] uppercase tracking-wider text-ele-muted">From the office: access</h2>
      <p className="mt-1 whitespace-pre-line text-sm text-ele-strong">{text}</p>
    </section>
  );
}

function RamsLink({ jobId, code, label }: { jobId: string; code: string; label: string }) {
  return (
    <>
      <h3 className="font-menu text-[11px] uppercase tracking-wider text-ele-body">Risk assessment and method statement (RAMS)</h3>
      <p className="mt-1 font-body text-[13px] text-ele-body">
        Applicable RAMS for this visit: <span className="text-ele-strong">{code}</span> {label}
      </p>
      <a
        href={api.jobs.ramsHref(jobId)}
        target="_blank"
        rel="noopener noreferrer"
        className="mt-2 inline-flex min-h-11 w-full items-center justify-center rounded-ele border border-ele-blue px-4 font-heading text-sm tracking-wide text-ele-blue"
      >
        Open the RAMS
      </a>
    </>
  );
}

function ReasonChips({ value, onPick }: { value: NotSurveyedReason | null; onPick: (r: NotSurveyedReason) => void }) {
  return (
    <div className="mt-2 grid grid-cols-3 gap-2" role="group" aria-label="Why not surveyed">
      {NOT_SURVEYED_REASON_CODES.map((r) => (
        <button key={r} type="button" aria-pressed={value === r} className={chip(value === r)} onClick={() => onPick(r)}>
          {NOT_SURVEYED_REASONS[r]}
        </button>
      ))}
    </div>
  );
}
